import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "bizpandit-ui";

import { authService } from "@/features/auth/services/auth.service";
import { AUTH_QUERY_KEYS } from "@/features/auth/constants";
import { pickHomeForRole } from "@/app/router/roleHome";
import { useAuthStore } from "@/stores/authStore";
import type { ApiError } from "@/api/types";
import type { PhoneVerificationIntent, VerifyOtpResponse } from "@/features/auth/types";

/** Normalize a raw 10-digit Indian mobile to E.164. */
export function toE164India(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (raw.startsWith("+")) return `+${digits}`;
  if (digits.startsWith("91") && digits.length === 12) return `+${digits}`;
  return `+91${digits}`;
}

/** Triggers backend OTP delivery (WhatsApp/SMS) and returns the normalized E.164 phone. */
export function useSendPhoneOtp() {
  return useMutation({
    // A string, or the phone plus the form's honeypot (decision #38).
    mutationFn: async (arg: string | { phone: string; website?: string }) => {
      const raw = typeof arg === "string" ? arg : arg.phone;
      const website = typeof arg === "string" ? undefined : arg.website;
      const phone = toE164India(raw);
      await authService.sendOtp({ phone, ...(website ? { website } : {}) });
      return { phone };
    },
    onError: (error: unknown) => {
      const message =
        (error as { message?: string })?.message ??
        "Couldn't send the verification code. Try again.";
      toast.error(message);
    },
  });
}

export interface VerifyPhoneOtpInput {
  phone: string;
  code: string;
  intent: PhoneVerificationIntent;
}

export function useVerifyPhoneOtp() {
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      phone,
      code,
      intent,
    }: VerifyPhoneOtpInput): Promise<VerifyOtpResponse> => {
      return authService.verifyOtp({ phone, code, intent });
    },
    onSuccess: (result) => {
      if (result.status === "LOGGED_IN") {
        setSession(result.user);
        queryClient.setQueryData(AUTH_QUERY_KEYS.me, result.user);
        toast.success(`Welcome back, ${result.user.name.split(" ")[0]}`);
        const from = (location.state as { from?: Location })?.from?.pathname;
        const redirectTo = from ?? pickHomeForRole(result.user.role);
        navigate(redirectTo, { replace: true });
      }
    },
    onError: (error: ApiError | Error) => {
      const message =
        (error as { message?: string })?.message ??
        "That code didn't match. Try again.";
      toast.error(message);
    },
  });
}
